<template>
  <div class="admin-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-4 mb-6">
      <div>
        <div class="d-flex align-center ga-2 mb-1">
          <v-chip color="purple" variant="flat" size="small" class="text-white font-weight-bold">
            ADMINISTRATION
          </v-chip>
          <v-chip size="small" variant="tonal">
            {{ users.length }} Users Registered
          </v-chip>
        </div>
        <h1 class="text-h4 font-weight-bold">User Accounts Management</h1>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex align-center flex-wrap ga-2">
        <v-btn
          color="primary"
          class="btn-modern font-weight-bold text-white"
          :disabled="!selectedRow"
          prepend-icon="mdi-pencil-outline"
          @click="openEditDialog"
        >
          Edit Selected
        </v-btn>

        <v-btn
          color="error"
          variant="tonal"
          class="btn-modern"
          :disabled="!selectedRow"
          prepend-icon="mdi-trash-can-outline"
          @click="deleteUser"
        >
          Delete User
        </v-btn>
      </div>
    </div>

    <!-- Data Table Card -->
    <v-card class="glass-card pa-4">
      <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
        <v-text-field
          v-model="search"
          placeholder="Search by username, email, company, status..."
          prepend-inner-icon="mdi-magnify"
          density="comfortable"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 360px"
          @input="applySearch"
        />
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        class="bg-transparent modern-admin-table"
        :search="search"
        :items-per-page="25"
        density="comfortable"
        show-select
        single-select
        v-model="selectedItems"
      >
        <!-- Status Column Custom Rendering -->
        <template #item.status="{ item }">
          <v-chip
            size="small"
            :color="item.status === 'admin' ? 'purple' : 'primary'"
            variant="tonal"
            class="font-weight-bold text-uppercase"
          >
            {{ item.status || 'User' }}
          </v-chip>
        </template>

        <!-- Active Column -->
        <template #item.is_active="{ item }">
          <v-chip
            size="x-small"
            :color="item.is_active ? 'success' : 'grey'"
            variant="flat"
            class="font-weight-bold"
          >
            {{ item.is_active ? 'ACTIVE' : 'INACTIVE' }}
          </v-chip>
        </template>

        <!-- Created At Column -->
        <template #item.created_at="{ item }">
          <span class="text-caption text-medium-emphasis">
            {{ formatDate(item.created_at) }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit User Dialog -->
    <v-dialog v-model="editDialog" max-width="650px" persistent>
      <v-card class="glass-card pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center ga-2">
            <v-icon color="primary" size="24">mdi-account-edit</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Edit User Account</h3>
          </div>
          <v-btn icon variant="text" size="small" @click="closeEditDialog">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.name" label="Username" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.email" label="Email Address" type="email" density="comfortable" variant="outlined" />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.firstname" label="First Name" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.lastname" label="Last Name" density="comfortable" variant="outlined" />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.company" label="Organization / Company" density="comfortable" variant="outlined" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="editedItem.status" label="Role / Status (e.g. admin, participant)" density="comfortable" variant="outlined" />
            </v-col>

            <v-col cols="12" sm="8">
              <v-select
                v-model="selectedCountry"
                :items="countries"
                item-title="name"
                item-value="code"
                label="Country"
                return-object
                density="comfortable"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="editedItem.countrycode" label="Country Code" readonly density="comfortable" variant="outlined" />
            </v-col>

            <v-col cols="12" sm="6">
              <v-switch v-model="editedItem.is_active" label="Account Active" color="primary" hide-details />
            </v-col>
            <v-col cols="12" sm="6">
              <v-switch v-model="editedItem.okcomm" label="Opt-in Communications (OKComm)" color="primary" hide-details />
            </v-col>
          </v-row>
        </v-form>

        <div class="d-flex justify-end ga-2 mt-6">
          <v-btn variant="tonal" class="btn-modern" @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="primary" class="btn-modern text-white font-weight-bold" @click="saveChanges">Save User</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api2 from '@/services/api2'
import Papa from 'papaparse'

export default {
  name: 'Admin',
  setup() {
    const store = useStore()
    const router = useRouter()
    const selectedRow = ref(null)
    const editDialog = ref(false)
    const editedItem = ref({})
    const selectedItems = ref([])

    const user = computed(() => store.getters['auth/user'])
    const users = ref([])
    const search = ref('')
    const filteredUsers = ref([])

    const selectedCountry = ref(null)
    const countries = ref([])

    const formatDate = (dateString) => {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }

    const loadCSV = async () => {
      try {
        const response = await fetch('countriesdb.csv')
        const csvData = await response.text()
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            countries.value = result.data.map(item => ({
              code: item.code,
              name: item.name,
            }))
          },
          error: (error) => {
            console.error('Error parsing CSV:', error)
          },
        })
      } catch (error) {
        console.error('Error fetching CSV:', error)
      }
    }

    onMounted(async () => {
      try {
        const response = await store.dispatch('auth/fetchAllUser')
        users.value = response || []
        filteredUsers.value = users.value
        loadCSV()
      } catch (error) {
        console.error('Failed to fetch users:', error)
        router.push('/login')
      }
    })

    const headers = [
      { title: 'ID', value: 'id', sortable: true },
      { title: 'Username', value: 'name', sortable: true },
      { title: 'Email', value: 'email', sortable: true },
      { title: 'First Name', value: 'firstname', sortable: true },
      { title: 'Last Name', value: 'lastname', sortable: true },
      { title: 'Company', value: 'company', sortable: true },
      { title: 'Country', value: 'countryname', sortable: true },
      { title: 'Status / Role', value: 'status', sortable: true },
      { title: 'Active', value: 'is_active', sortable: true },
      { title: 'Created', value: 'created_at', sortable: true },
    ]

    const applySearch = () => {
      if (search.value && search.value.trim()) {
        const q = search.value.toLowerCase()
        filteredUsers.value = users.value.filter(u =>
          Object.values(u).some(val =>
            String(val).toLowerCase().includes(q),
          ),
        )
      } else {
        filteredUsers.value = users.value
      }
    }

    watch(selectedItems, (newVal) => {
      if (newVal && newVal.length > 1) {
        selectedItems.value = [newVal[newVal.length - 1]]
      }
      selectedRow.value = newVal && newVal.length > 0 ? newVal[0] : null
    })

    watch(selectedCountry, (newValue) => {
      if (newValue && typeof newValue === 'object' && newValue.code) {
        editedItem.value.countryname = newValue.name
        editedItem.value.countrycode = newValue.code
      } else if (newValue) {
        editedItem.value.countrycode = newValue
      }
    })

    const openEditDialog = async () => {
      if (!selectedRow.value) return
      try {
        const userData = await store.dispatch('auth/fetchUserID', selectedRow.value)
        if (userData) {
          editDialog.value = true
          editedItem.value = {
            id: userData.id,
            name: userData.name || '',
            email: userData.email || '',
            firstname: userData.firstname || '',
            lastname: userData.lastname || '',
            company: userData.company || '',
            countryname: userData.countryname || '',
            countrycode: userData.countrycode || '',
            status: userData.status || '',
            is_active: userData.is_active || false,
            verified_at: userData.verified_at,
            updated_at: userData.updated_at,
            okcomm: userData.okcomm || false,
          }
          selectedCountry.value = editedItem.value.countryname
        }
      } catch (err) {
        console.error('Failed to load user for edit:', err)
      }
    }

    const closeEditDialog = () => {
      editDialog.value = false
      editedItem.value = {}
    }

    const saveChanges = async () => {
      try {
        const response = await api2.post(`/api/admin/users/${editedItem.value.id}`, editedItem.value)
        if (response.status === 200) {
          const index = users.value.findIndex(u => u.id === editedItem.value.id)
          if (index !== -1) {
            users.value[index] = { ...editedItem.value }
            filteredUsers.value = [...users.value]
          }
          closeEditDialog()
        }
      } catch (error) {
        console.error('Failed to update user:', error)
      }
    }

    const deleteUser = async () => {
      if (!selectedRow.value) return
      if (!confirm(`Are you sure you want to delete this user?`)) return
      try {
        const response = await api2.delete(`/api/admin/users/${selectedRow.value}`)
        if (response.status === 200) {
          users.value = users.value.filter(u => u.id !== selectedRow.value)
          filteredUsers.value = [...users.value]
          selectedRow.value = null
          selectedItems.value = []
        }
      } catch (error) {
        console.error('Failed to delete user:', error)
      }
    }

    return {
      user,
      users,
      headers,
      search,
      filteredUsers,
      applySearch,
      selectedRow,
      selectedItems,
      editDialog,
      editedItem,
      openEditDialog,
      closeEditDialog,
      saveChanges,
      deleteUser,
      selectedCountry,
      countries,
      formatDate,
    }
  },
}
</script>

<style scoped>
.admin-view {
  max-width: 1400px;
  margin: 0 auto;
}

.modern-admin-table :deep(th) {
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.modern-admin-table :deep(tr:hover) {
  background: rgba(99, 102, 241, 0.04) !important;
}
</style>
